import { env } from 'env-utils';
import { envToBoolean } from './utils/feature-flags-utils';

export enum FeatureFlagKeys {
  workflowsPage = 'workflowsPage',
  apiInventoryPage = 'apiInventoryPage',
  apiIssuesEnabled = 'apiIssuesEnabled',
  saasBomPage = 'saasBomPage',
  workflowsBetaBadge = 'workflowsBetaBadge',
  scheduledScansSettings = 'scheduledScansSettings',
  scheduledScanTimeSettings = 'scheduledScanTimeSettings',
  excludeFileOrFoldersSettings = 'excludeFileOrFoldersSettings',
  awsCodeCommitCloudformation = 'awsCodeCommitCloudformation',
  reslovedIssues = 'reslovedIssues',
  applicationsPdf = 'applicationsPdf',
  sbomPdf = 'sbomPdf',
  savedFilters = 'savedFilters',
  issuesPdf = 'issuesPdf',
  oxTrialPeriod = 'oxTrialPeriod',
  protectingXDevsBadge = 'protectingXDevsBadge',
  samlConnection = 'samlConnection',
  secretsManagement = 'secretsManagement',
  oxIsGitLabWebhookIntegrationAvaliable = 'oxIsGitLabWebhookIntegrationAvaliable',
  kubernetesConnectorFamily = 'kubernetesConnectorFamily',
  oxCanRunFastestScan = 'oxCanRunFastestScan',
  oxCanRunFastScan = 'oxCanRunFastScan',
  issueGraph = 'issueGraph',
  directEksConnection = 'directEksConnection',
  eksPrivateLink = 'eksPrivateLink',
  excludeByRuleIdFromAllRepos = 'excludeByRuleIdFromAllRepos',
  rbacAppOwners = 'rbacAppOwners',
  rbacTags = 'rbacTags',
  shouldViewIssueTrend = 'shouldViewIssueTrend',
  shouldTrackSeverity = 'shouldTrackSeverity',
  oxPipelineWorkflowsEnabled = 'oxPipelineWorkflowsEnabled',
  defineBranch = 'defineBranch',
  developerCountByCommits = 'developerCountByCommits',
  bomPageEnabled = 'bomPageEnabled',
}

export interface FeatureFlags {
  [FeatureFlagKeys.bomPageEnabled]: boolean;
  [FeatureFlagKeys.workflowsPage]: boolean;
  [FeatureFlagKeys.apiInventoryPage]: boolean;
  [FeatureFlagKeys.apiIssuesEnabled]: boolean;
  [FeatureFlagKeys.saasBomPage]: boolean;
  [FeatureFlagKeys.workflowsBetaBadge]: boolean;
  [FeatureFlagKeys.scheduledScanTimeSettings]: boolean;
  [FeatureFlagKeys.scheduledScansSettings]: boolean;
  [FeatureFlagKeys.excludeFileOrFoldersSettings]: boolean;
  [FeatureFlagKeys.awsCodeCommitCloudformation]: boolean;
  [FeatureFlagKeys.reslovedIssues]: boolean;
  [FeatureFlagKeys.applicationsPdf]: boolean;
  [FeatureFlagKeys.sbomPdf]: boolean;
  [FeatureFlagKeys.savedFilters]: boolean;
  [FeatureFlagKeys.issuesPdf]: boolean;
  [FeatureFlagKeys.oxTrialPeriod]: boolean;
  [FeatureFlagKeys.protectingXDevsBadge]: boolean;
  [FeatureFlagKeys.samlConnection]: boolean;
  [FeatureFlagKeys.secretsManagement]: boolean;
  [FeatureFlagKeys.kubernetesConnectorFamily]: boolean;
  [FeatureFlagKeys.oxCanRunFastestScan]: boolean;
  [FeatureFlagKeys.oxCanRunFastScan]: boolean;
  [FeatureFlagKeys.issueGraph]: boolean;
  [FeatureFlagKeys.directEksConnection]: boolean;
  [FeatureFlagKeys.eksPrivateLink]: boolean;
  [FeatureFlagKeys.oxIsGitLabWebhookIntegrationAvaliable]: boolean;
  [FeatureFlagKeys.excludeByRuleIdFromAllRepos]: boolean;
  [FeatureFlagKeys.rbacAppOwners]: boolean;
  [FeatureFlagKeys.shouldViewIssueTrend]: boolean;
  [FeatureFlagKeys.shouldTrackSeverity]: boolean;
  [FeatureFlagKeys.oxPipelineWorkflowsEnabled]: boolean;
  [FeatureFlagKeys.rbacTags]: boolean;
  [FeatureFlagKeys.defineBranch]: boolean;
  [FeatureFlagKeys.developerCountByCommits]: boolean;
}

export const OnPremFeatureFlags: Record<FeatureFlagKeys, boolean> = {
  bomPageEnabled: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_BOM_PAGE_ENABLED`],
    false,
  ),
  oxIsGitLabWebhookIntegrationAvaliable: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_GITLAB_WEBHOOK_INTEGRATION_SETTINGS`],
    false,
  ),
  workflowsPage: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_WORKFLOWS_PAGE`],
    false,
  ),
  [FeatureFlagKeys.apiInventoryPage]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_API_INVENTORY_PAGE`],
    false,
  ),
  [FeatureFlagKeys.apiIssuesEnabled]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_API_ISSUES_ENABLED`],
    false,
  ),
  [FeatureFlagKeys.saasBomPage]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_SAAS_BOM_PAGE`],
    false,
  ),
  workflowsBetaBadge: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_WORKFLOWS_BETA_BADGE`],
    true,
  ),
  scheduledScansSettings: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_SCHEDULED_SCANS_SETTINGS`],
    true,
  ),
  scheduledScanTimeSettings: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_SCHEDULED_SCAN_TIME_SETTINGS`],
    true,
  ),
  excludeFileOrFoldersSettings: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_EXCLUDE_FILE_OR_FOLDERS_SETTINGS`],
    false,
  ),
  awsCodeCommitCloudformation: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_AWS_CODE_COMMIT_CLOUDFORMATION`],
    true,
  ),
  reslovedIssues: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_RESOLVED_ISSUES`],
    false,
  ),
  savedFilters: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_SAVED_FILTERS`],
    false,
  ),
  issuesPdf: envToBoolean(env()[`REACT_APP_FEATURE_FLAG_ISSUES_PDF`], false),
  applicationsPdf: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_APPS_PDF`],
    false,
  ),
  sbomPdf: envToBoolean(env()[`REACT_APP_FEATURE_FLAG_SBOM_PDF`], false),
  [FeatureFlagKeys.samlConnection]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_SAML_CONNECTION`],
    false,
  ),
  oxTrialPeriod: false,
  protectingXDevsBadge: true,
  [FeatureFlagKeys.secretsManagement]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_SECRETS_MANAGEMENT`],
    false,
  ),
  [FeatureFlagKeys.kubernetesConnectorFamily]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_KUBERNETES_CONNECTOR_FAMILY`],
    false,
  ),
  [FeatureFlagKeys.oxCanRunFastestScan]: false,
  [FeatureFlagKeys.oxCanRunFastScan]: false,
  [FeatureFlagKeys.issueGraph]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_ISSUE_GRAPH`],
    false,
  ),
  [FeatureFlagKeys.directEksConnection]: false,
  [FeatureFlagKeys.eksPrivateLink]: false,
  [FeatureFlagKeys.excludeByRuleIdFromAllRepos]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_EXCLUDE_BY_RULE_ID_FROM_ALL_REPOS`],
    false,
  ),
  [FeatureFlagKeys.rbacAppOwners]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_RBAC_APP_OWNERS`],
    false,
  ),
  [FeatureFlagKeys.shouldViewIssueTrend]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_ISSUE_TREND`],
    false,
  ),
  [FeatureFlagKeys.shouldTrackSeverity]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_TRACK_SEVERITY`],
    false,
  ),
  [FeatureFlagKeys.oxPipelineWorkflowsEnabled]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_PIPELINE_WORKFLOWS_ENABLED`],
    false,
  ),
  [FeatureFlagKeys.rbacTags]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_RBAC_TAGS`],
    false,
  ),
  [FeatureFlagKeys.defineBranch]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_DEFINE_BRANCH`],
    false,
  ),
  [FeatureFlagKeys.developerCountByCommits]: envToBoolean(
    env()[`REACT_APP_FEATURE_FLAG_DEVELOPER_COUNT_BY_COMMITS`],
    false,
  ),
};
